import { Dropzone, Snackbar, Typography } from 'components';
import FadeIn from 'components/FadeIn';
import { CloudIcon, TextIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { ChangeEvent, DragEvent, FC, useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { cn } from 'utils/cn';

import { LOTTIE_WAITING } from 'constants/images';
import { handlerPreventAll } from 'handlers/handlerPreventAll';
import { usePathname } from 'next/navigation';
import { getExtension } from 'utils/getExtension';
import {
  setFileToUpload,
  updateUploadingError,
  useSettingCsvSeparatorContentFlow,
  useTextContentFlow,
  useUploadingContentFlow
} from './_actions';

interface ISelectionFlow {
  useTextContentFlow: () => void;
  setFileToUpload: (file: File) => void;
  useSettingCsvSeparatorContentFlow: () => void;
  useUploadingContentFlow: () => void;
  updateUploadingError: (error: string) => void;
  allowedTypes: string[];
  supportedFormats: string[];
}

const SelectionFlow: FC<ISelectionFlow> = ({
  useTextContentFlow: textContentFlow,
  setFileToUpload,
  useSettingCsvSeparatorContentFlow: settingCsvSeparatorContentFlow,
  useUploadingContentFlow: uploadingContentFlow,
  updateUploadingError,
  allowedTypes,
  supportedFormats
}) => {
  const pathname = usePathname();
  const t = useTranslations();

  const [dragOver, setDragOver] = useState(false);
  const dropboxRef = useRef<HTMLDivElement | null>(null);

  const handleFile = useCallback(
    (file: File) => {
      if (!file) return;

      setFileToUpload(file);
    },
    [setFileToUpload]
  );

  const handleFiles = useCallback(
    (files: FileList) => {
      if (!!files?.length) {
        const filesArray = Array.from(files);
        filesArray?.forEach((file) => handleFile(file));

        // NOTE: file.type has shown to be unreliable,
        // getting the extension is not perfect but seems to work better.
        const isNotAllowed = filesArray.some((file) => {
          const extension = getExtension(file);
          return !allowedTypes?.includes(extension);
        });

        if (isNotAllowed) {
          return updateUploadingError(t('ordering_form.error.invalid_type'));
        }

        const isCSV = filesArray.some((file) => {
          const extension = getExtension(file);
          return extension === 'csv';
        });

        if (isCSV) {
          return settingCsvSeparatorContentFlow();
        }

        uploadingContentFlow();
      }
    },
    [
      allowedTypes,
      handleFile,
      settingCsvSeparatorContentFlow,
      t,
      updateUploadingError,
      uploadingContentFlow
    ]
  );

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) handleFiles(e.target.files);
  };

  const drop = useCallback(
    (e: DragEvent) => {
      handlerPreventAll(e);

      if (!e.dataTransfer || !e.dataTransfer.files) return;
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
  };

  useEffect(() => {
    const currentRef = dropboxRef?.current;

    if (currentRef) {
      currentRef.addEventListener(
        'dragenter',
        handlerPreventAll as unknown as EventListener,
        false
      );
      // currentRef.addEventListener('dragover', handlerPreventAll as unknown as EventListener, false);
      currentRef.addEventListener('drop', drop as unknown as EventListener, false);

      return () => {
        currentRef.removeEventListener('dragenter', handlerPreventAll as unknown as EventListener);
        // currentRef.removeEventListener('dragover', handlerPreventAll as unknown as EventListener);
        currentRef.removeEventListener('drop', drop as unknown as EventListener);
      };
    }
  }, [drop, dropboxRef]);

  const addon = pathname.split('/translation/new/')?.at(1);

  if (addon)
    return (
      <div className="ordering_form__content_frame rounded shadow-custom">
        <h2>{t('ordering_form.uploader.downloading_document')}</h2>
        <div className="mt-12 grid place-content-center">
          <iframe width="200" height="200" src={LOTTIE_WAITING} />
        </div>
      </div>
    );

  return (
    <div
      className="ordering_form__content_frame rounded shadow-custom"
      id="dropbox"
      ref={dropboxRef}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <Typography variant="medium7xl" className="!mb-8 !mt-0 px-8 text-tertiary-100">
        {t('ordering_form.select_source')}
      </Typography>
      {dragOver ? (
        <FadeIn key={'dragover'}>
          <Dropzone />
        </FadeIn>
      ) : (
        <div className="flex flex-col gap-4 xl:flex-row">
          <div
            className={cn(
              'h-[208px] w-full rounded-3xl bg-tertiary-600/[.25] p-2 shadow-custom outline-none',
              'group',
              'transition-all duration-200 ease-in-out hover:scale-105'
            )}
          >
            <div
              onClick={textContentFlow}
              className={cn(
                'flex h-48 flex-col items-center justify-center',
                'cursor-pointer rounded',
                'duration-600 transition-all ease-in-out',
                'border-spacing-5 border border-dashed border-primary-blue-200 group-hover:border-primary-blue-200/100'
              )}
            >
              <TextIcon className="scale-[0.94]" />
              <div className="mt-2 items-center">
                <Typography
                  variant="medium4"
                  className="!mt-0 inline-block select-none text-primary-blue-100"
                >
                  {t('ordering_form.place_your_text')}
                </Typography>
              </div>
            </div>
          </div>
          <div
            className={cn(
              'h-[208px] w-full rounded-3xl bg-tertiary-600/[.25] p-2 shadow-custom outline-none',
              'group',
              'transition-all duration-200 ease-in-out hover:scale-105'
            )}
          >
            <form
              encType="multipart/form-data"
              className={cn(
                'flex h-full flex-col items-center justify-center',
                'cursor-pointer rounded',
                'duration-600 transition-all ease-in-out',
                'border-spacing-5 border border-dashed border-primary-blue-200 group-hover:border-opacity-100'
              )}
            >
              <input
                className="ordering_form__uploader__input"
                type="file"
                id="file"
                name="file"
                multiple
                onChange={handleFileInput}
              />
              <label className="flex w-full cursor-pointer flex-col items-center" htmlFor="file">
                <CloudIcon className="mt-[24px] scale-100" />
                <div className="mt-3 cursor-pointer items-center">
                  <Typography
                    variant="medium4"
                    className="!mt-0 inline-block w-4/5 select-none text-primary-blue-100"
                  >
                    {t('ordering_form.upload_a_document')}
                  </Typography>
                </div>
              </label>
            </form>
          </div>
        </div>
      )}
      <div>
        <Snackbar
          className="mt-12"
          name={t('ordering_form.supported_formats')}
          description={supportedFormats?.map(
            (f, index) => `${f}${index === supportedFormats.length - 1 ? '' : ', '}`
          )}
        />
      </div>
    </div>
  );
};

export default connect(
  ({ staticData: { supportedFormats, allowedTypes } }) => ({ supportedFormats, allowedTypes }),
  {
    useTextContentFlow,
    setFileToUpload,
    useSettingCsvSeparatorContentFlow,
    useUploadingContentFlow,
    updateUploadingError
  }
)(SelectionFlow);
